import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import Button from '../../../form/Button';
import { ModalFooter } from '../../../modal/layout/ModalFooter';
import {
    ModalScrollableBody,
    ModalScrollableFooter,
} from '../../../modal/layout/ModalScrollableLayout';
import { NOTIFICATION_VARIANT, useNotifications } from '../../../notifications/Notifications';
import { useProfile } from '../../../../profile';
import type { AttributeAsset, ImageListAttributeEntry } from '../contracts';
import { useAttributeMutations } from '../graphql/useAttributeMutations';
import { ATTRIBUTE_UPLOAD_ACTIONS, AttributeUploadError, uploadAttributeFiles } from '../uploadApi';
import type { AttributeModalContentProps } from './types';

interface ImageListAssetFormValue {
    assetId: string;
    url: string;
    name: string | null;
    caption: string;
}

interface ImageListAttributeFormValues {
    assets: ImageListAssetFormValue[];
}

const MAX_IMAGE_LIST_ITEMS = 8;

function toFormAsset(asset: AttributeAsset): ImageListAssetFormValue {
    return {
        assetId: asset.id,
        url: asset.url,
        name: asset.name,
        caption: asset.caption,
    };
}

function toFormValues(entry: ImageListAttributeEntry): ImageListAttributeFormValues {
    return {
        assets: entry.imageList.map(toFormAsset),
    };
}

export function AttributeImageListModalContent({
    entry,
    entityType,
    entityId,
    onClose,
    onAfterMutation,
    onFormStateChange,
    bodyIntro,
    scrollBodyRef,
}: AttributeModalContentProps<ImageListAttributeEntry>) {
    const formId = `attribute-image-list-${entry.key}`;
    const nextPendingIdRef = useRef(0);
    const pendingFilesRef = useRef(new Map<string, File>());
    const previewUrlsRef = useRef(new Map<string, string>());
    const profile = useProfile();
    const { addNotification } = useNotifications();
    const { setAssetCaption, reorderImages, deleteAsset, isMutating } = useAttributeMutations();
    const [isUploading, setIsUploading] = useState(false);
    const defaultValues = useMemo(() => toFormValues(entry), [entry]);
    const previousDefaultValuesRef = useRef(defaultValues);
    const {
        control,
        register,
        handleSubmit,
        getValues,
        reset,
        formState: { isDirty, isSubmitting },
    } = useForm<ImageListAttributeFormValues>({
        defaultValues,
    });
    const { fields, append, move, remove } = useFieldArray({
        control,
        name: 'assets',
        keyName: 'fieldId',
    });
    const isBusy = isSubmitting || isMutating || isUploading;

    useEffect(() => {
        // useForm already applies the initial defaults. Resetting on mount replaces
        // the field-array rows and can detach the target of the first interaction.
        if (previousDefaultValuesRef.current !== defaultValues) {
            previousDefaultValuesRef.current = defaultValues;
            reset(defaultValues);
        }
    }, [defaultValues, reset]);

    useEffect(
        () => () => {
            previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            previewUrlsRef.current.clear();
        },
        []
    );

    useEffect(() => {
        onFormStateChange({ isDirty, isBusy });
    }, [isBusy, isDirty, onFormStateChange]);

    function clearPendingFiles() {
        pendingFilesRef.current.clear();
        previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current.clear();
    }

    function stageFiles(files: FileList | null) {
        const selectedFiles = Array.from(files ?? []);
        if (selectedFiles.length === 0) {
            return;
        }

        if (getValues('assets').length + selectedFiles.length > MAX_IMAGE_LIST_ITEMS) {
            addNotification({
                message: gettext('ATTRIBUTE_IMAGE_LIST_LIMIT_MESSAGE'),
                variant: NOTIFICATION_VARIANT.ERROR,
                autoDismiss: true,
            });
            return;
        }

        append(
            selectedFiles.map((file) => {
                const assetId = `pending-${nextPendingIdRef.current}`;
                nextPendingIdRef.current += 1;
                const url = URL.createObjectURL(file);
                pendingFilesRef.current.set(assetId, file);
                previewUrlsRef.current.set(assetId, url);
                return { assetId, url, name: file.name, caption: '' };
            })
        );
    }

    async function saveImageList(values: ImageListAttributeFormValues) {
        const originalAssetsById = new Map(entry.imageList.map((asset) => [asset.id, asset]));
        const keptOriginalIds = new Set(
            values.assets
                .filter((asset) => originalAssetsById.has(asset.assetId))
                .map((asset) => asset.assetId)
        );
        const removedAssets = entry.imageList.filter((asset) => !keptOriginalIds.has(asset.id));
        const pendingAssets = values.assets.filter((asset) =>
            pendingFilesRef.current.has(asset.assetId)
        );
        const target = { entityType, entityId, key: entry.key };
        let savedAssets = entry.imageList;
        let hasSavedChanges = false;

        try {
            // Remove first so replacing images works even when the gallery is at its limit.
            await removedAssets.reduce(async (previous, asset) => {
                await previous;
                const updatedEntry = await deleteAsset(target, asset.id);
                if (updatedEntry.valueType !== 'image_list') {
                    throw new Error(`deleteAttributeAsset returned ${updatedEntry.valueType}.`);
                }
                savedAssets = updatedEntry.imageList;
                hasSavedChanges = true;
            }, Promise.resolve());

            const uploadedIds = new Map<string, string>();
            if (pendingAssets.length > 0) {
                setIsUploading(true);
                const uploadedEntry = await uploadAttributeFiles({
                    action: ATTRIBUTE_UPLOAD_ACTIONS.appendImages,
                    entityType,
                    entityId,
                    attributeKey: entry.key,
                    valueType: 'image_list',
                    files: pendingAssets.map(
                        (asset) => pendingFilesRef.current.get(asset.assetId)!
                    ),
                    csrfToken: profile.csrfToken,
                });
                if (uploadedEntry.valueType !== 'image_list' || !uploadedEntry.imageList) {
                    throw new Error(`appendAttributeImages returned ${uploadedEntry.valueType}.`);
                }
                const previousIds = new Set(savedAssets.map((asset) => asset.id));
                const newAssets = uploadedEntry.imageList.filter(
                    (asset) => !previousIds.has(asset.id)
                );
                if (newAssets.length !== pendingAssets.length) {
                    throw new Error('appendAttributeImages did not return all new images.');
                }
                pendingAssets.forEach((asset, index) =>
                    uploadedIds.set(asset.assetId, newAssets[index].id)
                );
                savedAssets = uploadedEntry.imageList;
                hasSavedChanges = true;
            }

            await values.assets.reduce(async (previous, asset) => {
                await previous;
                const assetId = uploadedIds.get(asset.assetId) ?? asset.assetId;
                const originalCaption = originalAssetsById.get(assetId)?.caption ?? '';
                if (asset.caption !== originalCaption) {
                    const updatedEntry = await setAssetCaption(target, assetId, asset.caption);
                    if (updatedEntry.valueType !== 'image_list') {
                        throw new Error(
                            `setAttributeAssetCaption returned ${updatedEntry.valueType}.`
                        );
                    }
                    savedAssets = updatedEntry.imageList;
                    hasSavedChanges = true;
                }
            }, Promise.resolve());

            const nextOrder = values.assets.map(
                (asset) => uploadedIds.get(asset.assetId) ?? asset.assetId
            );
            if (savedAssets.map((asset) => asset.id).join('|') !== nextOrder.join('|')) {
                const updatedEntry = await reorderImages(target, nextOrder);
                if (updatedEntry.valueType !== 'image_list') {
                    throw new Error(`reorderAttributeImages returned ${updatedEntry.valueType}.`);
                }
                savedAssets = updatedEntry.imageList;
                hasSavedChanges = true;
            }

            reset({ assets: savedAssets.map(toFormAsset) });
            clearPendingFiles();
        } catch (error) {
            if (hasSavedChanges) {
                reset({ assets: savedAssets.map(toFormAsset) });
                clearPendingFiles();
                try {
                    await onAfterMutation();
                } catch {
                    // Keep the original save error visible; the user can reopen to refresh.
                }
            }
            let errorMessage = gettext('ATTRIBUTE_SAVE_ERROR');
            if (error instanceof AttributeUploadError) {
                errorMessage = error.message;
            }
            if (hasSavedChanges) {
                errorMessage = gettext('ATTRIBUTE_PARTIAL_SAVE_ERROR');
            }
            addNotification({
                message: errorMessage,
                variant: NOTIFICATION_VARIANT.ERROR,
                autoDismiss: true,
            });
            return;
        } finally {
            setIsUploading(false);
        }

        await onAfterMutation();
        addNotification({
            message: gettext('SAVED_SUCCESSFULLY'),
            variant: NOTIFICATION_VARIANT.SUCCESS,
            autoDismiss: true,
        });
    }

    function stageRemoval(index: number) {
        const assetId = getValues(`assets.${index}.assetId`);
        remove(index);
        pendingFilesRef.current.delete(assetId);
        const previewUrl = previewUrlsRef.current.get(assetId);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrlsRef.current.delete(assetId);
        }
    }

    return (
        <>
            <ModalScrollableBody ref={scrollBodyRef}>
                {bodyIntro}
                <div className="space-y-5 px-6">
                    <div>
                        <label
                            className="block text-sm font-semibold text-gray-700"
                            htmlFor={`${formId}-files`}
                        >
                            {gettext('ATTRIBUTE_ADD_IMAGE_FILES_LABEL')}
                        </label>
                        <input
                            id={`${formId}-files`}
                            type="file"
                            accept="image/*"
                            multiple
                            disabled={isBusy || fields.length >= MAX_IMAGE_LIST_ITEMS}
                            onChange={(event) => {
                                stageFiles(event.target.files);
                                event.target.value = '';
                            }}
                            className="file:bg-primary mt-2 block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold disabled:opacity-50"
                        />
                    </div>

                    <form
                        id={formId}
                        onSubmit={(event) => void handleSubmit(saveImageList)(event)}
                        className="space-y-4"
                    >
                        {fields.length === 0 && (
                            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                                {gettext('ATTRIBUTE_NO_IMAGES_UPLOADED')}
                            </div>
                        )}

                        {fields.map((field, index) => (
                            <div
                                key={field.fieldId}
                                className="grid gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-[120px_1fr]"
                            >
                                <img
                                    src={field.url}
                                    alt={field.caption || field.name || entry.label}
                                    className="h-28 w-full rounded-lg object-cover ring-1 ring-gray-200 md:w-28"
                                />
                                <div className="space-y-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">
                                                {field.name ||
                                                    gettext('ATTRIBUTE_VALUE_TYPE_IMAGE')}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {interpolate(
                                                    gettext('ATTRIBUTE_POSITION_LABEL'),
                                                    { position: index + 1 },
                                                    true
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="neutral"
                                                className="px-3 py-2 text-xs"
                                                disabled={isBusy || index === 0}
                                                onClick={() => move(index, index - 1)}
                                            >
                                                {gettext('ATTRIBUTE_MOVE_UP_ACTION')}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="neutral"
                                                className="px-3 py-2 text-xs"
                                                disabled={isBusy || index === fields.length - 1}
                                                onClick={() => move(index, index + 1)}
                                            >
                                                {gettext('ATTRIBUTE_MOVE_DOWN_ACTION')}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                className="px-3 py-2 text-xs"
                                                disabled={isBusy}
                                                onClick={() => stageRemoval(index)}
                                            >
                                                {gettext('ATTRIBUTE_REMOVE_ACTION')}
                                            </Button>
                                        </div>
                                    </div>

                                    <label
                                        className="block text-sm font-semibold text-gray-700"
                                        htmlFor={`${formId}-${field.assetId}-caption`}
                                    >
                                        {gettext('ATTRIBUTE_CAPTION_FIELD')}
                                    </label>
                                    <textarea
                                        id={`${formId}-${field.assetId}-caption`}
                                        rows={2}
                                        disabled={isBusy}
                                        className="focus:border-primary focus:ring-primary/30 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-inner focus:outline-none focus:ring-2 disabled:bg-gray-100"
                                        {...register(`assets.${index}.caption`)}
                                    />
                                </div>
                            </div>
                        ))}
                    </form>
                </div>
            </ModalScrollableBody>
            <ModalScrollableFooter>
                <ModalFooter
                    formId={formId}
                    onClose={onClose}
                    saveButtonType="submit"
                    isLoading={isBusy}
                    saveDisabled={!isDirty}
                    saveLabel={gettext('ATTRIBUTE_SAVE_CHANGES_ACTION')}
                />
            </ModalScrollableFooter>
        </>
    );
}
